import { useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const DEFAULT_TAX_RATES = [
  { label: "No GST", rate: 0 }, { label: "GST 5%", rate: 5 }, { label: "GST 12%", rate: 12 },
  { label: "GST 18%", rate: 18 }, { label: "GST 28%", rate: 28 },
];

const FIELDS = [
  { key: "business_name", label: "Business name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "website", label: "Website" },
  { key: "tax_id", label: "GSTIN / Tax ID" },
  { key: "invoice_prefix", label: "Invoice prefix" },
  { key: "default_tax_rate", label: "Default GST %", type: "number" },
];

export default function SettingsModal({ profile, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    business_name: profile?.business_name || "",
    email: profile?.email || "",
    phone: profile?.phone || "",
    website: profile?.website || "",
    tax_id: profile?.tax_id || "",
    invoice_prefix: profile?.invoice_prefix || "INV-2025-",
    default_tax_rate: profile?.default_tax_rate || 18,
    address: profile?.address || "",
    default_terms: profile?.default_terms || "",
    footer_note: profile?.footer_note || "",
    default_unit: profile?.default_unit || "unit",
    tax_rates: profile?.tax_rates?.length ? profile.tax_rates : DEFAULT_TAX_RATES,
  }));
  const [busy, setBusy] = useState(false);
  const [hasLetterhead, setHasLetterhead] = useState(!!profile?.letterhead_path);
  const [letterheadVersion, setLetterheadVersion] = useState(Date.now());
  const [uploadingLetterhead, setUploadingLetterhead] = useState(false);
  const letterheadRef = useRef(null);
  const letterheadUrl = `/api/business/letterhead?v=${letterheadVersion}`;

  useEffect(() => {
    if (!profile) {
      api.get("/business_profile").then((r) => {
        setForm((prev) => ({ ...prev, ...r.data, tax_rates: r.data?.tax_rates?.length ? r.data.tax_rates : DEFAULT_TAX_RATES }));
        setHasLetterhead(!!r.data?.letterhead_path);
      }).catch(() => {});
    }
  }, [profile]);

  const uploadLetterhead = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingLetterhead(true);
    try {
      const body = new FormData();
      body.append("file", file);
      await api.post("/business/letterhead", body);
      setHasLetterhead(true);
      setLetterheadVersion(Date.now());
      toast.success("Letterhead uploaded. It now appears on your invoices.");
      onSaved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not upload letterhead");
    } finally {
      setUploadingLetterhead(false);
      if (letterheadRef.current) letterheadRef.current.value = "";
    }
  };

  const removeLetterhead = async () => {
    try {
      await api.delete("/business/letterhead");
      setHasLetterhead(false);
      toast.success("Letterhead removed");
      onSaved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not remove letterhead");
    }
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const cleanRates = (form.tax_rates || [])
        .map((r) => ({ label: (r.label || "").trim(), rate: Number(r.rate) || 0 }))
        .filter((r) => r.label);
      await api.put("/business_profile", { ...form, default_tax_rate: Number(form.default_tax_rate), tax_rates: cleanRates });
      toast.success("Business profile saved");
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update settings");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <section className="create-modal" role="dialog" aria-modal="true" data-testid="settings-modal">
        <div className="modal-heading">
          <div>
            <p className="eyebrow">FLOWSPHERE / SETTINGS</p>
            <h2>Business profile</h2>
            <p>This information appears on every invoice you send.</p>
          </div>
          <button className="icon-button" onClick={onClose} data-testid="settings-close-button"><X size={18} /></button>
        </div>
        <form onSubmit={save}>
          <div className="modal-fields">
            {FIELDS.map((field) => (
              <label key={field.key} className="modal-field">
                {field.label}
                <input type={field.type || "text"} value={form[field.key] ?? ""} data-testid={`settings-${field.key}-input`} onChange={(e) => setForm({ ...form, [field.key]: e.target.value })} />
              </label>
            ))}
            <label className="modal-field wide-field">
              Business address
              <input value={form.address} data-testid="settings-address-input" onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </label>
            <label className="modal-field wide-field">
              Default invoice terms
              <input value={form.default_terms} data-testid="settings-terms-input" onChange={(e) => setForm({ ...form, default_terms: e.target.value })} />
            </label>
            <label className="modal-field">
              Default product unit
              <select data-testid="settings-default-unit-input" value={form.default_unit} onChange={(e) => setForm({ ...form, default_unit: e.target.value })}>
                <option value="unit">unit</option>
                <option value="piece">piece</option>
                <option value="box">box</option>
                <option value="pack">pack</option>
                <option value="kg">kg (kilogram)</option>
                <option value="gram">gram</option>
                <option value="litre">litre</option>
                <option value="month">month</option>
                <option value="hour">hour</option>
              </select>
            </label>
            <label className="modal-field wide-field">
              Invoice footer note
              <input value={form.footer_note} data-testid="settings-footer-input" onChange={(e) => setForm({ ...form, footer_note: e.target.value })} />
            </label>
          </div>

          <div className="letterhead-section" data-testid="tax-rates-section">
            <div className="letterhead-heading">
              <div>
                <strong>Tax rates</strong>
                <p>Define the tax rates your team can pick when creating invoices, sales orders and purchases.</p>
              </div>
              <button
                type="button"
                className="link-button"
                data-testid="tax-rate-add-button"
                onClick={() => setForm((f) => ({ ...f, tax_rates: [...(f.tax_rates || []), { label: "", rate: 0 }] }))}
              >
                <Plus size={14} /> Add rate
              </button>
            </div>
            <div className="tax-rate-rows">
              {(form.tax_rates || []).map((rate, index) => (
                <div className="tax-rate-row" key={index} data-testid={`tax-rate-row-${index}`}>
                  <input
                    data-testid={`tax-rate-label-${index}`}
                    placeholder="Label (e.g. GST 18%)"
                    value={rate.label}
                    onChange={(e) => setForm((f) => ({ ...f, tax_rates: f.tax_rates.map((r, i) => (i === index ? { ...r, label: e.target.value } : r)) }))}
                  />
                  <input
                    data-testid={`tax-rate-value-${index}`}
                    type="number"
                    min="0"
                    step="any"
                    placeholder="%"
                    value={rate.rate}
                    onChange={(e) => setForm((f) => ({ ...f, tax_rates: f.tax_rates.map((r, i) => (i === index ? { ...r, rate: e.target.value } : r)) }))}
                  />
                  <span className="tax-rate-suffix">%</span>
                  <button
                    type="button"
                    className="icon-button danger-icon"
                    data-testid={`tax-rate-remove-${index}`}
                    onClick={() => setForm((f) => ({ ...f, tax_rates: f.tax_rates.filter((_, i) => i !== index) }))}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {(form.tax_rates || []).length === 0 && <p className="tax-rate-empty">No custom rates — the standard GST list will be used.</p>}
            </div>
          </div>

          <div className="letterhead-section" data-testid="letterhead-section">
            <div className="letterhead-heading">
              <div>
                <strong>Invoice letterhead</strong>
                <p>Upload your branded header (logo, business name, contact). It appears at the top of every A4 invoice.</p>
              </div>
              {hasLetterhead && (
                <button type="button" className="link-button danger" onClick={removeLetterhead} data-testid="letterhead-remove-button">
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </div>
            {hasLetterhead ? (
              <div className="letterhead-preview" data-testid="letterhead-preview">
                <img src={letterheadUrl} alt="Invoice letterhead" />
              </div>
            ) : (
              <button type="button" className="letterhead-dropzone" onClick={() => letterheadRef.current?.click()} data-testid="letterhead-upload-button">
                <ImagePlus size={22} />
                <strong>{uploadingLetterhead ? "Uploading…" : "Upload letterhead image"}</strong>
                <small>PNG, JPG or WEBP · up to 6 MB · A4 width recommended</small>
              </button>
            )}
            {hasLetterhead && (
              <button type="button" className="secondary-button letterhead-replace" onClick={() => letterheadRef.current?.click()} data-testid="letterhead-replace-button">
                {uploadingLetterhead ? "Uploading…" : "Replace letterhead"}
              </button>
            )}
            <input ref={letterheadRef} type="file" accept="image/png,image/jpeg,image/webp" hidden data-testid="letterhead-file-input" onChange={uploadLetterhead} />
          </div>

          <div className="modal-actions">
            <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
            <button className="primary-button" disabled={busy} data-testid="settings-save-button">{busy ? "Saving…" : "Save profile"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}
